import React, { useEffect, useState, useMemo } from "react";
import { CampaignMassUpdateProps } from "../campaign-action-type";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { campaignEditableFields } from "../../campaign-filter/campaign-filter-constant";
import { AlertComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import {
  SelectInputComponent,
  RevenueInputComponent,
} from "../../../../common/components";
import { dynamicFieldValidationSchema } from "../input-validation";
import SearchableSelectOwner from "../../../../common/components/select-owner/select-owner-compoent";
import "../../../leads/lead-actions/actions.scss";
import "../../../leads/lead-view/lead-view-details/lead-view-details-edit/lead-view-edit-view-full-info/lead-view-edit-view-full-info.scss";
import { makeStyles } from "@mui/styles";
import { useLeadOwnerOptions } from "../../../../utils/getLeadOwnerOptions";
import { OwnerOption } from "../../create-campaign/create-campaign";
import { getUserDetails } from "../../../../api/lead";
import {
  TYPE_OPTIONS,
  STATUS_OPTIONS,
} from "../../../../constants/campaignDropdown";
import DatePickerComponent from "../../../../common/components/date-picker/date-picker-compoenent";
import dayjs, { Dayjs } from "dayjs";
import leadInfoIcon from "../../../../assets/icons/leadInfo.svg";
import { massUpdateOfCampaigns } from "../../../../api/campaign";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const MassUpdateOfCampaigns: React.FC<CampaignMassUpdateProps> = ({
  campaignIds,
  isOpen,
  setMenuItems,
  selectedActions,
  selectedLeadActionScope,
  setSelectedCampaignsMap,
  fetchCampaigns,
  totalRecords,
  handleMassActionMenuClose,
}) => {
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions();
  const [selectedField, setSelectedField] = useState<{
    label: string;
    id: number;
    value: string;
  }>({
    label: "",
    id: 0,
    value: "",
  });
  const [inputValue, setInputValue] = useState<string>("");
  const [disableUpdate, setDisableUpdate] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [selectedInterestedProduct, setSelectedInterestedProduct] = useState(
    []
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true if screen ≤ 600px
  const dropdownLabels = [
    "Campaign Owner",
    "Status",
    "Type",
    "Start And End Date",
    "Actual Cost",
    "Expected Revenue",
    "Budgeted Cost",
  ];
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState<null | string>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    startdate: Dayjs | null;
    enddate: Dayjs | null;
  }>({
    startdate: null,
    enddate: null,
  });
  const [dateErrors, setDateErrors] = useState<{
    startdate?: string;
    enddate?: string;
  }>({});

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "View Campaigns");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));
        setLeadOptions(formattedOptions);
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, []);

  const validateFieldInput = async () => {
    const fieldKey = selectedField.value;
    const schema = dynamicFieldValidationSchema[fieldKey];
    if (!schema) return true;

    try {
      let valueToValidate;

      if (fieldKey === "startAndEndDate") {
        valueToValidate = selectedDateRange; // 👈 updated here
      } else if (fieldKey === "campaignOwner") {
        valueToValidate = selectedOwnerId;
      } else {
        valueToValidate = inputValue;
      }

      await schema.validate(valueToValidate, { abortEarly: false });
      setDateErrors({});
      setError("");
      return true;
    } catch (err: any) {
      if (fieldKey === "startAndEndDate") {
        const fieldErrors: { startdate?: string; enddate?: string } = {};
        err.inner?.forEach((e: any) => {
          if (e.path === "startdate") fieldErrors.startdate = e?.message;
          if (e.path === "enddate") fieldErrors.enddate = e?.message;
        });
        setDateErrors(fieldErrors);
      } else {
        setError(err?.message);
      }

      return false;
    }
  };

  const handleCancel = () => {
    setSelectedField({
      label: "",
      id: 0,
      value: "",
    });
    setInputValue("");
    setSelectedOwnerId(null);
    setSelectedDateRange({ startdate: null, enddate: null });
    setMenuItems((prevItems) =>
      prevItems.map((item) =>
        item.value === selectedActions
          ? { ...item, isOpen: !item.isOpen }
          : item
      )
    );
    handleMassActionMenuClose();
    setDisableUpdate(true);
  };

  const formatPayLoad = () => {
    let fieldToUpdate;

    if (selectedField.value === "campaignOwner") {
      fieldToUpdate = { campaignOwnerId: selectedOwnerId };
    } else if (selectedField.value === "startAndEndDate") {
      fieldToUpdate = {
        startDate: selectedDateRange?.startdate,
        endDate: selectedDateRange?.enddate,
      };
    } else {
      fieldToUpdate = { [selectedField.value]: inputValue };
    }

    const data = {
      campaignIds,
      fieldToUpdate,
    };

    return data;
  };

  const handleUpdate = async () => {
    const isValid = await validateFieldInput();
    if (!isValid) return;
    setLoading(true);
    setError("");
    let response;
    try {
      response = await massUpdateOfCampaigns(formatPayLoad());
      if (response?.status === 200) {
        setAlertMessage(
          `${campaignIds?.length === 1 ? "Campaign" : "Campaign(s)"} updated successfully.`
        );
        setAlertType("success");
        setSelectedCampaignsMap({});
        fetchCampaigns();
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the campaign."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setAlertOpen(true);
      handleCancel();
    }
  };

  const handleChange = (
    _: any,
    newValue: { label: string; id: number; value: string } | null
  ) => {
    setSelectedField(
      newValue ? { ...newValue } : { label: "", id: 0, value: "" }
    );
    setDisableUpdate(!newValue);
    setSelectedInterestedProduct([]);
    setInputValue("");
    setSelectedOwnerId(null);
    setSelectedDateRange({ startdate: null, enddate: null });
    setError("");
    setDateErrors({});
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target?.value;
    if (e.target.name === "description") {
      setInputValue(input?.slice(0, 150));
    } else if (e.target.name === "expectedResponse") {
      setInputValue(input?.slice(0, 50));
    } else setInputValue(e.target.value);
    setError("");
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const useStyles = makeStyles(() => ({
    selected: {},
    rootMenuItem: {
      "&$selected": {
        backgroundColor: "transparent !important",
        "&:hover": {
          backgroundColor: "#8879501A !important",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
  }));

  const fetchPlaceHolder = () => {
    if (selectedField?.label) {
      return `Enter ${selectedField?.label}`;
    }
  }

  return (
    <div className="mass-update-wrapper campaign-mass-update-wrapper">
      <Dialog
        open={isOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="update-popup-wrapper"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "800px",
              maxWidth: "95vw",
              minHeight:  "280px",
              borderBottomLeftRadius: '30px',
              borderBottomRightRadius: '30px',
              top: 0,  
              margin: 0,
              position: "absolute",
            },
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">{"Update"}</DialogTitle>
        <DialogContent>
          <div className="select-update-wrapper campaign-select-update-wrapper">
            <div className="field-select-wrapper">
              <Autocomplete
                disablePortal={true}
                options={[...campaignEditableFields]}
                getOptionLabel={(option) => option.label}
                onChange={handleChange}
                value={
                  [...campaignEditableFields].find(
                    (opt) => opt.label === selectedField?.label
                  ) || null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a field"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                  />
                )}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1 },
                  },
                }}
                sx={{
                  width: 250,
                  "& .MuiOutlinedInput-root": {
                    paddingRight: 0,
                    paddingLeft: "9px",
                    padding: 0,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7D7D7D", // ✅ Default border color
                    },
                  },
                  "& .MuiAutocomplete-input": {
                    padding: "7.5px 5px 7.5px 10px !important",
                  },
                }}
              />
            </div>
            <div className="field-value-wrapper">
              {selectedField?.label &&
                !dropdownLabels.includes(selectedField?.label) && (
                  <>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      required
                      onChange={handleInput}
                      value={inputValue}
                      name={selectedField?.value}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "37px",
                        },
                        paddingLeft: "15px",
                      }}
                      placeholder={fetchPlaceHolder() || "Enter value"}
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </>
                )}
              {selectedField?.label &&
                selectedField?.label === "Campaign Owner" && (
                  <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                    <div className="campaign-list-view-details-view-box-full-info-data-section-sub campaign-owner-dropdown">
                      <SearchableSelectOwner
                        fieldName="campaignOwner"
                        options={leadOptions}
                        // isShowError={!inputValue}
                        placeholder="Select Owner"
                        optional={true}
                        value={
                          leadOptions.find(
                            (opt) => opt.value === selectedOwnerId
                          ) || null
                        }
                        onChange={(selectedOption: OwnerOption | null) => {
                          if (selectedOption) {
                            setSelectedOwnerId(selectedOption?.value);
                            setError("");
                          } else {
                            setSelectedOwnerId(null);
                          }
                        }}
                      />
                    </div>
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label && selectedField?.label === "Type" && (
                <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                  <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                    <SelectInputComponent
                      fieldName="type"
                      value={inputValue}
                      onChange={(e: any) => {
                        setInputValue(e);
                        setError("");
                      }}
                      options={TYPE_OPTIONS}
                      optional={true}
                      // isShowError={!values.type}
                      placeholder="Select Type"
                    />
                  </div>
                  {error ? <div className="error-message">{error}</div> : ""}
                </div>
              )}
              {selectedField?.label && selectedField?.label === "Status" && (
                <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                  <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                    <SelectInputComponent
                      fieldName="status"
                      value={inputValue}
                      onChange={(e: any) => {
                        setInputValue(e);
                        setError("");
                      }}
                      options={STATUS_OPTIONS}
                      optional={true}
                      placeholder="Select Status"
                    />
                  </div>
                  {error ? <div className="error-message">{error}</div> : ""}
                </div>
              )}
              {selectedField?.label &&
                selectedField?.label === "Start And End Date" && (
                  <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                    <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                      <div className="campaign-list-view-details-view-box-full-info-data-section-sub date-picker">
                        <DatePickerComponent
                          fieldName="startdate"
                          value={selectedDateRange.startdate}
                          onChange={(newValue) => {
                            setSelectedDateRange((prev) => ({
                              ...prev,
                              startdate: newValue,
                            }));
                            if (newValue)
                              setDateErrors((prev) => ({
                                startdate: "",
                                enddate: selectedDateRange?.enddate
                                  ? ""
                                  : dateErrors?.enddate,
                              }));
                          }}
                          minDate={dayjs("1900-01-01")}
                          maxDate={selectedDateRange?.enddate || undefined}
                          optional={true}
                          isLabel={false}
                          useFormikError={false}
                          label="start date"
                        />
                      </div>
                      {dateErrors?.startdate ? (
                        <div className="error-message">
                          {dateErrors?.startdate}
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                      <div className="campaign-list-view-details-view-box-full-info-data-section-sub date-picker">
                        <DatePickerComponent
                          fieldName="enddate"
                          value={selectedDateRange.enddate}
                          onChange={(newValue) => {
                            setSelectedDateRange((prev) => ({
                              ...prev,
                              enddate: newValue,
                            }));
                            if (newValue)
                              setDateErrors((prev) => ({
                                enddate: "",
                                startdate: selectedDateRange?.startdate
                                  ? ""
                                  : dateErrors?.startdate,
                              }));
                          }}
                          minDate={
                            selectedDateRange?.startdate
                              ? dayjs(selectedDateRange.startdate).isBefore(
                                  dayjs("1900-01-01")
                                )
                                ? dayjs("1900-01-01")
                                : dayjs(selectedDateRange.startdate)
                              : undefined
                          }
                          optional={true}
                          isLabel={false}
                          useFormikError={false}
                          label="end date"
                        />
                      </div>
                      {dateErrors?.enddate ? (
                        <div className="error-message">
                          {dateErrors?.enddate}
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Actual Cost" && (
                  <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                    <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                      <RevenueInputComponent
                        fieldName="actualCost"
                        optional={false}
                        disabled={false}
                        value={inputValue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setInputValue(input);
                            setError("");
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder="Enter Actual Cost"
                        useFormikError={false}
                        label=""
                      />
                    </div>
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Expected Revenue" && (
                  <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                    <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                      <RevenueInputComponent
                        fieldName="expectedRevenue"
                        optional={false}
                        disabled={false}
                        value={inputValue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setInputValue(input);
                            setError("");
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder="Enter Expected Revenue"
                        useFormikError={false}
                        label=""
                      />
                    </div>
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Budgeted Cost" && (
                  <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                    <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                      <RevenueInputComponent
                        fieldName="budgetCost"
                        optional={false}
                        disabled={false}
                        value={inputValue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setInputValue(input);
                            setError("");
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder="Enter Budgeted Cost"
                        useFormikError={false}
                        label=""
                      />
                    </div>
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="button-wrapper">
          <Button onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>
          <Button
            disabled={disableUpdate}
            onClick={handleUpdate}
            autoFocus
            className="update-btn"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default MassUpdateOfCampaigns;
